import React, { Fragment, useState } from "react";
import { Box, InputLabel, useTheme, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "../../utils/axios.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface MemoryRowProps {
  memory: string;
  id: string;
  onMemoryUpdated?: () => void;
}

const MemoryRow: React.FC<MemoryRowProps> = (props) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [displayText, setDisplayText] = useState(props.memory);
  const theme = useTheme();

  const handleEditSubmit = async (newMemoryText: string) => {
    await axios
      .patch(
        `/v1/memory/${props.id}/`,
        JSON.stringify({ memory: newMemoryText }),
      )
      .then((_) => {
        setDisplayText(newMemoryText);
        props.onMemoryUpdated?.();
        handleCloseDialog();
      });
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  // Close the dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <Fragment>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box
          component="span"
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            mt: "2px",
          }}
        >
          {"\u2022"}
        </Box>
        <InputLabel
          sx={{
            flex: 1,
            minWidth: 0,
            mb: 0,
            color: theme.palette.text.primary,
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {displayText}
        </InputLabel>
        <Tooltip title="Edit" arrow>
          <IconButton
            onClick={handleOpenDialog}
            size="small"
            aria-label="edit-memory"
            sx={{ mt: "-2px" }}
          >
            <EditIcon
              fontSize="small"
              style={{ color: theme.palette.primary.main }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <TallyDialog
        open={isDialogOpen}
        title={i18n._(
          msg({
            context: "Title of a dialog enabling the user to edit a memory.",
            message: "Edit memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Subtitle of a dialog explaining how the application uses the memory to improve responses in the future.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversations",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title of the edit memory dialog.",
            message: "Save",
          }),
        )}
        onSubmit={handleEditSubmit}
        initialInputValue={displayText}
        handleDialogClose={handleCloseDialog}
      />
    </Fragment>
  );
};

export default MemoryRow;
